import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AnimationEvent, KeyboardEvent } from 'react';
import {
  addDays, addMonths, addYears, clampDay, endOfWeek, formatFull, inRange, makeDate, monthGrid, monthName,
  sameDay, startOfWeek, today as todayFn, weekdayNames,
} from './dateUtils';
import './form.css';

/**
 * Month calendar with DevExtreme-style zoom levels: days, then months (click
 * the title), then years. Used inline or inside DateField's popup.
 *
 * Keyboard (focus is on the grid, one cell is tabbable):
 *   arrows        day / week (months and years: one cell / one row of four)
 *   PageUp/Down   month (Shift: year); in the month and year grids, year / decade
 *   Home/End      start / end of the week
 *   Enter, Space  pick the day, or zoom in on a month / year
 *   Escape        onEscape (DateField closes the popup), from anywhere in the
 *                 calendar, the arrows and the footer included, so it never
 *                 also reaches a dialog around it
 *   Alt+Up, F4    onEscape too (the desktop DateEdit's keys to close it)
 *
 * `onSelect` fires only when a person picks a day.
 *
 * With no value it opens on today, or on the allowed day nearest today when
 * today is outside min/max.
 *
 * `showToday` and `onClear` add the desktop DateEdit's footer: Today picks
 * today (This month with pick="month") and is off when today is outside
 * min/max; Clear empties the field (only for fields that may be empty).
 *
 * `pick="month"` (month-only fields, DateBox calendarOptions zoom level
 * "year"): the calendar opens on the month grid and picking a month selects
 * the 1st of that month. The day grid is never shown.
 *
 * Motion, as DevExtreme's calendar: moving to another month (or year, or
 * decade) slides the grid in from the side you are travelling to, with the
 * old grid sliding out; zooming to another view pops the new view in from
 * 60% scale and opacity. 250ms each. A move during a slide starts a new one
 * from where the calendar now is. With prefers-reduced-motion the grid swaps
 * at once.
 */
export interface CalendarProps {
  value: Date | null;
  onSelect: (day: Date) => void;
  min?: Date | null;
  max?: Date | null;
  /** Focus the current day when it mounts (DateField's popup). */
  focusOnMount?: boolean;
  onEscape?: () => void;
  className?: string;
  /** What a pick selects: a day (default) or a month (its 1st). */
  pick?: 'day' | 'month';
  /** A Today button under the grid (This month with pick="month"). */
  showToday?: boolean;
  /** A Clear button under the grid; it calls this. */
  onClear?: () => void;
}

type View = 'month' | 'year' | 'decade';

/** Which page of its view a date is on: a month, a year or a decade. */
function pageOf(view: View, d: Date) {
  const yr = d.getFullYear();
  return view === 'month' ? yr * 12 + d.getMonth() : view === 'year' ? yr : Math.floor(yr / 10);
}

type Motion =
  | { kind: 'slide'; dir: 1 | -1; from: { view: View; focused: Date } }
  | { kind: 'zoom' };

/** The page on screen, and the motion that brought it there (null once done). */
interface Shown { view: View; page: number; focused: Date; seq: number; motion: Motion | null }

function reducedMotion() {
  try {
    return typeof window === 'undefined' || !window.matchMedia
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

const WEEKDAYS = weekdayNames('short');
const WEEKDAYS_LONG = weekdayNames('long');

function monthOutOfRange(y: number, m: number, min: Date | null, max: Date | null) {
  return (!!max && makeDate(y, m, 1) > max) || (!!min && makeDate(y, m + 1, 0) < min);
}

function yearOutOfRange(y: number, min: Date | null, max: Date | null) {
  return (!!max && makeDate(y, 0, 1) > max) || (!!min && makeDate(y, 11, 31) < min);
}

export default function Calendar({
  value, onSelect, min = null, max = null, focusOnMount, onEscape, className, pick: pickLevel = 'day', showToday = false, onClear,
}: CalendarProps) {
  const now = todayFn();
  const monthPick = pickLevel === 'month';
  const [view, setView] = useState<View>(monthPick ? 'year' : 'month');
  // No value: today, or the allowed day nearest it (max when today is past it).
  const [focused, setFocused] = useState<Date>(() => value ?? clampDay(now, min, max));
  const gridRef = useRef<HTMLDivElement>(null);
  // Move DOM focus to the focused cell after a keyboard move (or on mount when
  // asked), but never steal focus from elsewhere on the page.
  const wantFocus = useRef(!!focusOnMount);

  useLayoutEffect(() => {
    if (!wantFocus.current) return;
    wantFocus.current = false;
    gridRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
  });

  const y = focused.getFullYear();
  const m = focused.getMonth();
  const decadeStart = Math.floor(y / 10) * 10;

  // A new page or view starts its motion in the same render, so the incoming
  // grid (which takes DOM focus) mounts once and is not replaced afterwards.
  const page = pageOf(view, focused);
  const [shown, setShown] = useState<Shown>(() => ({ view, page, focused, seq: 0, motion: null }));
  if (shown.view !== view || shown.page !== page) {
    let next: Motion | null = null;
    if (!reducedMotion()) {
      next = shown.view !== view
        ? { kind: 'zoom' }
        : { kind: 'slide', dir: page > shown.page ? 1 : -1, from: { view: shown.view, focused: shown.focused } };
    }
    setShown({ view, page, focused, seq: next ? shown.seq + 1 : shown.seq, motion: next });
  }
  const motion = shown.view === view && shown.page === page ? shown.motion : null;
  const endMotion = (seq: number) => setShown((s) => (s.seq === seq && s.motion ? { ...s, motion: null } : s));
  // animationend does not come when the popup is hidden or the tab is in the
  // background; never leave the old grid behind.
  useEffect(() => {
    if (!shown.motion) return undefined;
    const seq = shown.seq;
    const t = window.setTimeout(() => setShown((s) => (s.seq === seq && s.motion ? { ...s, motion: null } : s)), 400);
    return () => window.clearTimeout(t);
  }, [shown.motion, shown.seq]);

  const moveTo = (d: Date) => {
    wantFocus.current = true;
    setFocused(d);
  };

  const pick = (d: Date) => {
    if (!inRange(d, min, max)) return;
    setFocused(d);
    onSelect(d);
  };

  const pickMonth = (d: Date) => {
    if (monthOutOfRange(d.getFullYear(), d.getMonth(), min, max)) return;
    setFocused(d);
    onSelect(makeDate(d.getFullYear(), d.getMonth(), 1));
  };

  const zoomIn = (d: Date, to: View) => {
    wantFocus.current = true;
    setFocused(d);
    setView(to);
  };

  const step = (dir: -1 | 1) => {
    if (view === 'month') setFocused(addMonths(focused, dir));
    else if (view === 'year') setFocused(addYears(focused, dir));
    else setFocused(addYears(focused, dir * 10));
  };

  const prevDisabled = view === 'month'
    ? monthOutOfRange(addMonths(focused, -1).getFullYear(), addMonths(focused, -1).getMonth(), min, max)
    : view === 'year' ? yearOutOfRange(y - 1, min, max)
      : !!min && makeDate(decadeStart - 1, 11, 31) < min;
  const nextDisabled = view === 'month'
    ? monthOutOfRange(addMonths(focused, 1).getFullYear(), addMonths(focused, 1).getMonth(), min, max)
    : view === 'year' ? yearOutOfRange(y + 1, min, max)
      : !!max && makeDate(decadeStart + 10, 0, 1) > max;

  const unit = view === 'month' ? 'month' : view === 'year' ? 'year' : 'decade';
  const title = view === 'month' ? `${monthName(m)} ${y}` : view === 'year' ? String(y) : `${decadeStart}-${decadeStart + 9}`;

  const onKeyDown = (e: KeyboardEvent) => {
    const k = e.key;
    if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); onEscape?.(); return; }
    // F4 and Alt+Up close (closeOnKey); an Alt+arrow is never a move.
    if (k === 'F4' || e.altKey) { closeOnKey(e); return; }
    let next: Date | null = null;
    if (view === 'month') {
      if (k === 'ArrowLeft') next = addDays(focused, -1);
      else if (k === 'ArrowRight') next = addDays(focused, 1);
      else if (k === 'ArrowUp') next = addDays(focused, -7);
      else if (k === 'ArrowDown') next = addDays(focused, 7);
      else if (k === 'Home') next = startOfWeek(focused);
      else if (k === 'End') next = endOfWeek(focused);
      else if (k === 'PageUp') next = e.shiftKey ? addYears(focused, -1) : addMonths(focused, -1);
      else if (k === 'PageDown') next = e.shiftKey ? addYears(focused, 1) : addMonths(focused, 1);
      else if (k === 'Enter' || k === ' ') { e.preventDefault(); pick(focused); return; }
    } else {
      const span = view === 'year' ? (n: number) => addMonths(focused, n) : (n: number) => addYears(focused, n);
      if (k === 'ArrowLeft') next = span(-1);
      else if (k === 'ArrowRight') next = span(1);
      else if (k === 'ArrowUp') next = span(-4);
      else if (k === 'ArrowDown') next = span(4);
      else if (k === 'PageUp') next = view === 'year' ? addYears(focused, -1) : addYears(focused, -10);
      else if (k === 'PageDown') next = view === 'year' ? addYears(focused, 1) : addYears(focused, 10);
      else if (k === 'Enter' || k === ' ') {
        e.preventDefault();
        if (monthPick && view === 'year') pickMonth(focused);
        else zoomIn(focused, view === 'year' ? 'month' : 'year');
        return;
      }
    }
    if (next) {
      e.preventDefault();
      moveTo(next);
    }
  };

  // Escape, Alt+Up and F4 on the arrows, the title or the footer close the
  // popup too, as on a day. Without this, Escape there went on to a dialog
  // around the field, and MUI's Dialog closed itself, calendar and all.
  function closeOnKey(e: KeyboardEvent) {
    if (!onEscape) return;
    if (e.key === 'Escape' || e.key === 'F4' || (e.altKey && e.key === 'ArrowUp')) {
      e.preventDefault();
      e.stopPropagation();
      onEscape();
    }
  }

  const todayOff = monthPick ? monthOutOfRange(now.getFullYear(), now.getMonth(), min, max) : !inRange(now, min, max);

  const cellClass = (opts: { other?: boolean; today?: boolean; selected?: boolean; focused?: boolean; disabled?: boolean }) =>
    ['app-cal-cell',
      opts.other && 'app-cal-cell--other',
      opts.today && 'app-cal-cell--today',
      opts.selected && 'app-cal-cell--selected',
      opts.focused && 'app-cal-cell--focused',
      opts.disabled && 'app-cal-cell--disabled'].filter(Boolean).join(' ');

  // One page of a view. `live` is the page you work with; the other is the
  // page sliding out, which is hidden from assistive tech and inert.
  const renderGrid = (v: View, f: Date, live: boolean) => {
    const y = f.getFullYear();
    const m = f.getMonth();
    const decadeStart = Math.floor(y / 10) * 10;
    const title = v === 'month' ? `${monthName(m)} ${y}` : v === 'year' ? String(y) : `${decadeStart}-${decadeStart + 9}`;
    const ref = live ? gridRef : undefined;
    if (v === 'month') {
      const days = monthGrid(y, m);
      return (
        <div className="app-cal-grid" role="grid" aria-label={title} ref={ref}>
          <div role="row" className="app-cal-row app-cal-head">
            {WEEKDAYS.map((d, i) => <div key={d} role="columnheader" className="app-cal-colhead" aria-label={WEEKDAYS_LONG[i]}>{d}</div>)}
          </div>
          {Array.from({ length: 6 }, (_, w) => (
              <div key={w} role="row" className="app-cal-row">
                {days.slice(w * 7, w * 7 + 7).map((d) => {
                  const disabled = !inRange(d, min, max);
                  const isFocused = live && sameDay(d, f);
                  const selected = sameDay(d, value);
                  return (
                    <div
                      key={d.getTime()}
                      role="gridcell"
                      className={cellClass({ other: d.getMonth() !== m, today: sameDay(d, now), selected, focused: isFocused, disabled })}
                      aria-selected={selected}
                      aria-disabled={disabled || undefined}
                      aria-label={formatFull(d)}
                      aria-current={sameDay(d, now) ? 'date' : undefined}
                      tabIndex={live ? (isFocused ? 0 : -1) : undefined}
                      onClick={live ? () => pick(d) : undefined}
                      onKeyDown={live ? onKeyDown : undefined}
                    >
                      <span className="app-cal-mark">{d.getDate()}</span>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      );
    }
    const cells = v === 'year'
      ? Array.from({ length: 12 }, (_, i) => ({
        date: makeDate(y, i, Math.min(f.getDate(), makeDate(y, i + 1, 0).getDate())),
        text: monthName(i, 'short'),
        label: `${monthName(i)} ${y}`,
        other: false,
        current: now.getFullYear() === y && now.getMonth() === i,
        selected: !!value && value.getFullYear() === y && value.getMonth() === i,
        isFocused: live && i === m,
        disabled: monthOutOfRange(y, i, min, max),
      }))
      : Array.from({ length: 12 }, (_, i) => {
        const yr = decadeStart - 1 + i;
        return {
          date: addYears(f, yr - y),
          text: String(yr),
          label: String(yr),
          other: yr < decadeStart || yr > decadeStart + 9,
          current: now.getFullYear() === yr,
          selected: !!value && value.getFullYear() === yr,
          isFocused: live && yr === y,
          disabled: yearOutOfRange(yr, min, max),
        };
      });
    return (
      <div className={`app-cal-grid app-cal-grid--${v === 'year' ? 'months' : 'years'}`} role="grid" aria-label={title} ref={ref}>
          {Array.from({ length: 3 }, (_, r) => (
            <div key={r} role="row" className="app-cal-row">
              {cells.slice(r * 4, r * 4 + 4).map((c) => (
                <div
                  key={c.label}
                  role="gridcell"
                  className={cellClass({ other: c.other, today: c.current, selected: c.selected, focused: c.isFocused, disabled: c.disabled })}
                  aria-selected={c.selected}
                  aria-disabled={c.disabled || undefined}
                  aria-label={c.label}
                  tabIndex={live ? (c.isFocused ? 0 : -1) : undefined}
                  onClick={live ? () => {
                    if (c.disabled) return;
                    if (monthPick && v === 'year') pickMonth(c.date);
                    else zoomIn(c.date, v === 'year' ? 'month' : 'year');
                  } : undefined}
                  onKeyDown={live ? onKeyDown : undefined}
                >
                  <span className="app-cal-mark">{c.text}</span>
                </div>
              ))}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className={`app-cal${className ? ` ${className}` : ''}`}>
      <div className="app-cal-nav">
        <button type="button" className="app-cal-arrow" aria-label={`Previous ${unit}`} disabled={prevDisabled} onClick={() => step(-1)} onKeyDown={closeOnKey}>
          <i className="bi bi-chevron-left" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="app-cal-title"
          aria-live="polite"
          disabled={view === 'decade'}
          onClick={() => setView(view === 'month' ? 'year' : 'decade')}
          onKeyDown={closeOnKey}
        >
          {title}
        </button>
        <button type="button" className="app-cal-arrow" aria-label={`Next ${unit}`} disabled={nextDisabled} onClick={() => step(1)} onKeyDown={closeOnKey}>
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
      </div>
      <div className="app-cal-views">
        {motion?.kind === 'slide' && (
          <div
            key={`out-${shown.seq}`}
            className={`app-cal-view app-cal-view--leaving app-cal-view--out-${motion.dir > 0 ? 'next' : 'prev'}`}
            aria-hidden="true"
            inert
          >
            {renderGrid(motion.from.view, motion.from.focused, false)}
          </div>
        )}
        <div
          key={shown.seq}
          className={`app-cal-view${motion ? ` app-cal-view--${motion.kind === 'zoom' ? 'pop' : `in-${motion.dir > 0 ? 'next' : 'prev'}`}` : ''}`}
          onAnimationEnd={(e: AnimationEvent) => { if (e.target === e.currentTarget) endMotion(shown.seq); }}
        >
          {renderGrid(view, focused, true)}
        </div>
      </div>
      {(showToday || onClear) && (
        <div className="app-cal-footer">
          {showToday && (
            <button
              type="button"
              className="app-cal-footer-btn"
              disabled={todayOff}
              onClick={() => (monthPick ? pickMonth(now) : pick(now))}
              onKeyDown={closeOnKey}
            >
              {monthPick ? 'This month' : 'Today'}
            </button>
          )}
          {onClear && (
            <button type="button" className="app-cal-footer-btn" disabled={!value} onClick={onClear} onKeyDown={closeOnKey}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
